import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus } from 'shared/icons';
import api from '../../services/api';
import Button from '../ui/Button';
import ErrorCard from '../ui/ErrorCard';
import Input from '../ui/Input';
import Modal from '../ui/Modal';
import { MONO_CLASS, Mono } from '../ui/Mono';
import { SkeletonTable } from '../ui/Skeleton';
import StandardTable from '../ui/StandardTable';
import Textarea from '../ui/Textarea';
import { useToast } from '../ui/Toast';
import { ParseSettingsEntries } from './payload';
import { SETTING_LABEL_PREFIX, useWarehouseLabels } from './warehouseLabels';

function stringifyValue(value, pretty = false) {
    try {
        return pretty ? JSON.stringify(value, null, 2) : JSON.stringify(value);
    } catch {
        return String(value);
    }
}

/**
 * Aba "Parâmetros" (`wms_settings`): tabela chave → valor (JSON) editável,
 * upsert via PUT /warehouse/settings {key, value}. Sem DELETE, sem active —
 * parâmetro é configuração viva (os de rota do seed aparecem aqui).
 */
const SettingsManager = () => {
    const toast = useToast();
    const { label } = useWarehouseLabels();
    /**
     * Rótulo PT-BR do parâmetro (`wms.setting.<chave>`), ou string vazia quando
     * a chave não tem rótulo — caso de parâmetro que o dono criou pela UI. A aba
     * segue agnóstica: sem rótulo ela mostra a chave e o aviso, nunca a chave
     * técnica do dicionário.
     */
    const settingLabel = useCallback(
        (key) => {
            const dictionaryKey = `${SETTING_LABEL_PREFIX}${key}`;
            const text = label(dictionaryKey);
            return text === dictionaryKey ? '' : text;
        },
        [label],
    );
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [isModalOpen, setModalOpen] = useState(false);
    const [editingKey, setEditingKey] = useState(null);
    const [keyInput, setKeyInput] = useState('');
    const [valueInput, setValueInput] = useState('');
    const [labelInput, setLabelInput] = useState('');
    const [formError, setFormError] = useState(null);
    const [saving, setSaving] = useState(false);

    // Sem `label` nas deps de propósito: a mensagem amigável é composta no
    // render — a chegada do dicionário remoto de rótulos não deve refazer o GET.
    const loadEntries = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const data = await api.getWarehouseSettings({ limit: 500 });
            setEntries(ParseSettingsEntries(data));
        } catch (error) {
            setLoadError(error?.message || 'Erro ao carregar');
            setEntries([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadEntries();
    }, [loadEntries]);

    const openCreate = () => {
        setEditingKey(null);
        setKeyInput('');
        setValueInput('');
        setLabelInput('');
        setFormError(null);
        setModalOpen(true);
    };

    const openEdit = useCallback((entry) => {
        setEditingKey(entry.key);
        setKeyInput(entry.key);
        setValueInput(stringifyValue(entry.value, true));
        setLabelInput(entry.label || '');
        setFormError(null);
        setModalOpen(true);
    }, []);

    const closeModal = () => {
        if (!saving) {
            setModalOpen(false);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const key = keyInput.trim();
        if (!key) {
            setFormError({ field: 'key', message: label('wms.common.required') });
            return;
        }
        let parsedValue;
        try {
            parsedValue = JSON.parse(valueInput);
        } catch {
            setFormError({ field: 'value', message: label('wms.common.invalid_json') });
            return;
        }

        setSaving(true);
        setFormError(null);
        try {
            await api.putWarehouseSetting(key, parsedValue, labelInput.trim());
            toast.success('Parâmetro salvo');
            setModalOpen(false);
            await loadEntries();
        } catch (error) {
            const message =
                error?.code === 'NETWORK_ERROR' ? label('wms.collector.error_network') : error?.message || 'Erro ao salvar';
            setFormError({ message });
        } finally {
            setSaving(false);
        }
    };

    const columns = useMemo(
        () => [
            {
                key: 'key',
                label: label('wms.field.key'),
                // Busca/exportação casam pelos DOIS: o dono procura tanto por
                // "vão" quanto por `bulky_min_bay_width`.
                // Rótulo por-linha (entry.label) vence o dicionário global; a
                // busca casa pelos dois + a chave crua.
                getValue: (entry) => `${entry.label || settingLabel(entry.key)} ${entry.key}`,
                render: (entry) => {
                    const friendly = entry.label || settingLabel(entry.key);
                    return (
                        <div className="flex flex-col gap-0.5">
                            <span className="text-black dark:text-white">
                                {friendly || (
                                    <span className="italic text-mono-400 dark:text-mono-500">
                                        {label('wms.settings.unlabeled')}
                                    </span>
                                )}
                            </span>
                            <Mono className="text-xs text-mono-500 dark:text-mono-400">{entry.key}</Mono>
                        </div>
                    );
                },
            },
            {
                key: 'value',
                label: label('wms.field.value'),
                getValue: (entry) => stringifyValue(entry.value),
                render: (entry) => (
                    <Mono className="block max-w-md truncate" title={stringifyValue(entry.value, true)}>
                        {stringifyValue(entry.value)}
                    </Mono>
                ),
            },
            {
                key: 'actions',
                label: label('wms.common.actions'),
                sortable: false,
                searchable: false,
                exportable: false,
                getValue: () => '',
                render: (entry) => (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={(event) => {
                            event.stopPropagation();
                            openEdit(entry);
                        }}
                    >
                        {label('wms.common.edit')}
                    </Button>
                ),
            },
        ],
        [label, openEdit, settingLabel],
    );

    return (
        <section className="space-y-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div className="space-y-1">
                    <h2 className="text-lg font-semibold text-black dark:text-white">{label('wms.settings.title')}</h2>
                    <p className="max-w-3xl text-sm text-mono-600 dark:text-mono-400">
                        {label('wms.settings.description')}
                    </p>
                </div>
                <Button onClick={openCreate} className="w-full sm:w-auto">
                    <Plus size={18} className="mr-2" />
                    {`${label('wms.common.new')} — Parâmetro`}
                </Button>
            </div>

            {loading ? (
                <SkeletonTable columns={3} rows={6} />
            ) : loadError ? (
                <ErrorCard
                    title={label('wms.settings.title')}
                    message={`${label('wms.common.load_error')}\n${loadError}`}
                    action={label('wms.common.retry')}
                    actionFn={loadEntries}
                />
            ) : (
                <StandardTable
                    rows={entries}
                    columns={columns}
                    rowKey={(entry) => entry.key}
                    countLabel="parâmetros"
                    exportFileName="wms-parametros"
                    emptyMessage="Nenhum parâmetro encontrado."
                />
            )}

            <Modal
                isOpen={isModalOpen}
                onClose={closeModal}
                title={editingKey ? `${label('wms.common.edit')} — Parâmetro` : `${label('wms.common.new')} — Parâmetro`}
                size="lg"
            >
                <form onSubmit={handleSubmit} className="space-y-4">
                    <Input
                        label={label('wms.field.key')}
                        value={keyInput}
                        onChange={(event) => setKeyInput(event.target.value)}
                        placeholder="ex.: route.speed_mps"
                        errorMessage={formError?.field === 'key' ? formError.message : undefined}
                        disabled={saving || Boolean(editingKey)}
                        autoComplete="off"
                        className={MONO_CLASS}
                    />
                    <div>
                        <Input
                            label="Rótulo (opcional)"
                            value={labelInput}
                            onChange={(event) => setLabelInput(event.target.value)}
                            placeholder="ex.: Largura mínima do vão para volumoso (m)"
                            disabled={saving}
                            autoComplete="off"
                        />
                        <p className="mt-1 text-xs text-mono-500 dark:text-mono-400">
                            Nome amigável do parâmetro. Vazio = usa o rótulo padrão (se houver) ou a chave técnica.
                        </p>
                    </div>
                    <div>
                        <Textarea
                            label={label('wms.field.value')}
                            value={valueInput}
                            onChange={(event) => setValueInput(event.target.value)}
                            rows={6}
                            errorMessage={formError?.field === 'value' ? formError.message : undefined}
                            disabled={saving}
                            className={MONO_CLASS}
                        />
                        <p className="mt-1 text-xs text-mono-500 dark:text-mono-400">
                            JSON — ex.: 1.4, true, "FROM_DOCK" ou {'{"a": 1}'}.
                        </p>
                    </div>
                    {formError && !formError.field ? (
                        <p role="alert" className="text-sm text-danger-600 dark:text-danger-400">
                            {formError.message}
                        </p>
                    ) : null}
                    <div className="flex flex-col gap-3 pt-4 sm:flex-row">
                        <Button type="submit" loading={saving} className="flex-1">
                            {label('wms.common.save')}
                        </Button>
                        <Button type="button" onClick={closeModal} variant="secondary" className="sm:w-auto">
                            {label('wms.common.cancel')}
                        </Button>
                    </div>
                </form>
            </Modal>
        </section>
    );
};

export default SettingsManager;
